import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as d3 from "d3";
import { rccColors } from "../colors";
import D3SingleLineChart from "./D3SingleLineChart";
import { appService } from "../../App/app.service";
import {
  getDefaultSalesType,
  getDefaultSelectedFiscalQuarters,
  getDefaultSelectedStage,
} from "../../util/customFunctions";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";
import RccCard from "../RccCard";

/**
 * Displays trend of average deal size over quarters.
 * Inputs: Redux state (averageDealSizeData, company).
 * Outputs: Single line chart of average deal size.
 */
function AverageDealSizeTrend({ title, redirectToDashboard }) {
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [timeKey, setTimeKey] = useState(null);
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);
  const [isDefaultTeamLoaded, setIsDefaultTeamLoaded] = useState(false);

  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
    fetchDefaultTeamFilter()
      .then((teams) => {
        setDefaultTeamFilter(teams);
        setIsDefaultTeamLoaded(true);
      })
      .catch((e) => {
        console.error("Error fetching default team filter:", e);
        setIsDefaultTeamLoaded(true);
      });
  }, []);

  const getTimeKeyFromFiltersResponse = (filtersResp) => {
    const message = filtersResp?.message;
    if (!Array.isArray(message)) return null;
    const meta = message.find(
      (item) =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        typeof item.defaultTimeFrame === "string"
    );
    return meta?.defaultTimeFrame || null;
  };

  const getTimeKeyFromDataResponse = (dataResp) => {
    const dataArray = dataResp?.data || dataResp;
    if (!Array.isArray(dataArray)) return null;
    const marker = dataArray.find(
      (item) =>
        item &&
        typeof item === "object" &&
        !Array.isArray(item) &&
        typeof item.type === "string"
    );
    return marker?.type || null;
  };

  const isDataLoadedForKey = (dataResp, key) => {
    if (!key) return false;
    const dataArray = dataResp?.data || dataResp;
    if (!Array.isArray(dataArray) || dataArray.length <= 1) return false;
    return getTimeKeyFromDataResponse(dataArray) === key;
  };

  // Get data from Redux store - using existing averageDealSizeData
  const company = useSelector((state) => state.app.company);
  const currencySymbol = useSelector((state) => state.app.user?.Currency);
  const averageDealSizeData = useSelector(
    (state) => state.app.averageDealSizeData || ""
  );

  // Check if data is loading
  const loadedKey = useMemo(
    () => getTimeKeyFromDataResponse(averageDealSizeData?.data),
    [averageDealSizeData]
  );
  const effectiveTimeKey = timeKey || loadedKey || "closed_fiscal_quarter";
  const isDataLoaded = isDataLoadedForKey(
    averageDealSizeData,
    effectiveTimeKey
  );

  const isLoading = !isDataLoaded || !isFirstLevelTeamsLoaded || !isDefaultTeamLoaded;

  // Fetch data if not available
  useEffect(() => {
    if (fetchingRef.current) return;

    // If we already have data for the correct timeframe and filters applied, do nothing.
    if (isDataLoaded && appliedFilters.length > 0 && timeKey) return;

    if (!isFirstLevelTeamsLoaded || !isDefaultTeamLoaded) return;

    fetchingRef.current = true;
    dispatch({ type: "get_average_deal_size_filters_request" });

    appService
      .getAverageDealSizeFilters()
      .then((resp) => {
        dispatch({
          type: "get_average_deal_size_filters_success",
          json: resp,
        });

        const inferredTimeKey =
          getTimeKeyFromFiltersResponse(resp) || "closed_fiscal_quarter";
        setTimeKey(inferredTimeKey);

        // ---- Default stage logic ----
        const stageSequence = getDefaultSelectedStage(
          resp.message.filter((f) => f.type === "stage")
        );

        const defaultStages = resp.message
          .filter((item) => item.sequence >= stageSequence)
          .map((i) => i.stage_name)
          .filter(Boolean);

        // ---- Base filters ----
        let filters = [
          {
            name: inferredTimeKey,
            value: getDefaultSelectedFiscalQuarters(
              resp.message,
              inferredTimeKey
            ),
            string: true,
          },
          {
            name: "cycle_time_from_stage",
            value: defaultStages,
          },
        ];

        // ---- Company-specific logic ----
        if (company === "C0008") {
          const salesFilters = getDefaultSalesType(
            resp.message.filter((s) => s.type === "cross_sell1__c"),
            "cross_sell1__c"
          );

          const dealsFilters = getDefaultSalesType(
            resp.message.filter((s) => s.type === "Type"),
            "Type"
          );

          if (salesFilters[0] !== "All") {
            filters.push({
              name: "cross_sell1__c",
              value: salesFilters,
            });
          }

          if (dealsFilters[0] !== "All") {
            filters.push({
              name: "Type",
              value: dealsFilters,
            });
          }
        }

        // ---- Other defaults ----
        const defaultOppType = getDefaultSalesType(
          resp.message.filter((i) => i.type === "Type"),
          "Type"
        );

        const defaultSolutionCat = getDefaultSalesType(
          resp.message.filter((i) => i.type === "Solution_Cat"),
          "Solution_Cat"
        );

        if (defaultOppType[0] !== "All") {
          filters.push({ name: "Type", value: defaultOppType });
        }

        if (defaultSolutionCat[0] !== "All") {
          filters.push({
            name: "Solution_Cat",
            value: defaultSolutionCat,
          });
        }

        // Prioritize first-level teams, fallback to default team filter if not available
        const teamsToFilter =
          Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
            ? firstLevelTeamNames
            : (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
                ? defaultTeamFilter
                : []);

        if (Array.isArray(teamsToFilter) && teamsToFilter.length > 0) {
          filters.push({ name: "team", value: teamsToFilter });
        }

        setAppliedFilters(filters);
        dispatch({ type: "current_selected_filters", filters });

        // ---- Fetch analysis data (if needed) ----
        const shouldFetchData = !isDataLoadedForKey(
          averageDealSizeData,
          inferredTimeKey
        );
        if (!shouldFetchData) return null;

        dispatch({ type: "get_average_deal_size_data_request" });
        return appService.getAverageDealSizeData(
          resp.message.length === 0 ? [] : filters,
          inferredTimeKey
        );
      })
      .then((dataResp) => {
        fetchingRef.current = false;
        if (!dataResp) return;
        dispatch({
          type: "get_average_deal_size_data_success",
          json: dataResp,
        });
      })
      .catch((error) => {
        fetchingRef.current = false;
        dispatch({
          type: "get_average_deal_size_data_failure",
          error: typeof error === "object" ? "Something went wrong" : error,
        });
      });
  }, [
    company,
    dispatch,
    isDataLoaded,
    appliedFilters.length,
    timeKey,
    isFirstLevelTeamsLoaded,
    isDefaultTeamLoaded,
    firstLevelTeamNames,
    defaultTeamFilter,
  ]);

  // Transform data for chart - extract totals by quarter from data[1]
  const chartData = useMemo(() => {
    if (!isDataLoaded) {
      return [];
    }

    const data = averageDealSizeData.data;

    // data[1] contains totals by quarter
    const totalsByQuarter = data[1] || [];

    if (!Array.isArray(totalsByQuarter) || totalsByQuarter.length === 0) {
      return [];
    }

    // Determine which time field to use (backend adds `{ type: <key> }`)
    const timeField = getTimeKeyFromDataResponse(data) || effectiveTimeKey;

    // Transform to chart format: [{ quarter: string, value: number }]
    return totalsByQuarter
      .map((item) => {
        const quarter = item[timeField];
        const avgDealSize = item.avg_deal_size;

        if (!quarter || avgDealSize === undefined || avgDealSize === null) {
          return null;
        }

        return {
          quarter,
          value: Math.round(avgDealSize),
        };
      })
      .filter((item) => item !== null)
      .sort((a, b) => {
        // Sort by time label (works for both quarter strings and YYYY-MM month strings)
        return a.quarter.localeCompare(b.quarter);
      });
  }, [averageDealSizeData, isDataLoaded, effectiveTimeKey]);

  const handleRedirect = () => {
    console.log("Redirecting to Deal Type Dashboard...");
  };

  const handleZoom = () => {
    console.log("Zooming Deal Type Chart...");
  };

  return (
    <RccCard
      title={title || "Average Deal Size Trend"}
      isLoading={isLoading}
      redirectToDashboard={redirectToDashboard || "Average Deal Size Analysis"}
      onZoomClick={handleZoom}
      appliedFilters={appliedFilters}
      focusVariant={"current"}
    >
      <div
        style={{
          flex: 1,
          width: "100%",
          minHeight: "300px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chartData.length > 0 ? (
          <D3SingleLineChart
            data={chartData}
            options={{
              lineColor: rccColors.stackedBarPairedPalette[0].color,
              lineStyle: "dotted",
              legendLabel: "Average Deal Size",
              yAxisFormatter: (v) => {
                if (Math.abs(v) >= 1000) {
                  return `${currencySymbol}${d3.format(",.1f")(v / 1000)}K`;
                }
                return `${currencySymbol}${d3.format(",")(v)}`;
              },
              valueFormatter: (v) => {
                if (v === null || v === undefined) return "";
                return `${currencySymbol}${d3.format(",.1f")(v / 1000)}K`;
              },
              showValueLabels: true,
              showLegend: true,
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: rccColors.neutral[400],
            }}
          >
            No data available
          </div>
        )}
      </div>
    </RccCard>
  );
}

export default AverageDealSizeTrend;
